import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import TourCard from '../components/Tours/TourCard';
import { 
  MapPinIcon, 
  ClockIcon, 
  UsersIcon,
  CameraIcon,
  PhotoIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { tourService } from '../services/tourService';
import { useTheme } from '../contexts/ThemeContext';
import { applyFeaturedOrder } from '../utils/applyFeaturedOrder';

const PhotographyTours: React.FC = () => {
  const { actualTheme } = useTheme();
  const [photographyTours, setPhotographyTours] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get Photography tours from the service
    const loadTours = async () => {
      try {
        setLoading(true);
        const tours = await tourService.getToursByCategory('Photography');
        // Apply featured order
        const orderedTours = applyFeaturedOrder(tours);
        setPhotographyTours(orderedTours);
      } catch (error) {
        console.error('Error loading Photography tours:', error);
        setPhotographyTours([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadTours();
  }, []);

  // Listen for storage changes to update tours when admin makes changes (from other tabs only)
  useEffect(() => {
    let isHandling = false;
    const handleStorageChange = async (e: StorageEvent) => {
      // Only react to storage events from other tabs/windows, not our own
      if (e.key && (e.key.startsWith('tours_') || e.key === 'tours_cache') && !isHandling) {
        isHandling = true;
        try {
          setLoading(true);
          const tours = await tourService.getToursByCategory('Photography');
          // Apply featured order
          const orderedTours = applyFeaturedOrder(tours);
          setPhotographyTours(orderedTours);
        } catch (error) {
          console.error('Error loading Photography tours on storage change:', error);
          setPhotographyTours([]);
        } finally {
          setLoading(false);
          setTimeout(() => { isHandling = false; }, 1000); // Prevent rapid successive calls
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // No mock data - using only real admin data

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Floating Header Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-10 md:pt-12">
        <div className={`rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 shadow-lg ${
          actualTheme === 'dark' 
            ? 'bg-gradient-to-br from-primary-900 to-primary-800 border border-primary-700/50' 
            : 'bg-gradient-to-br from-primary-800 to-primary-900 border border-primary-700/30'
        } text-white`}>
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
              Photography Tours
            </h1>
            <p className="text-sm sm:text-base md:text-lg text-primary-100 max-w-2xl mx-auto leading-relaxed">
              Capture Africa's incredible wildlife and landscapes with expert photography guidance. 
              Learn from professional photographers while creating stunning images of your safari experience.
            </p>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Our Photography Tours?</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our photography experiences are designed to help you capture the perfect shot 
              while learning professional techniques from expert wildlife photographers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <CameraIcon className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-2">Expert Guidance</h3>
              <p className="text-neutral-600">Learn from professional wildlife photographers with years of experience</p>
            </div>
            <div className="text-center">
              <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <PhotoIcon className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-2">Perfect Timing</h3>
              <p className="text-neutral-600">Tours timed for optimal lighting and wildlife activity periods</p>
            </div>
            <div className="text-center">
              <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <UsersIcon className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-2">Small Groups</h3>
              <p className="text-neutral-600">Intimate group sizes for personalized photography instruction</p>
            </div>
            <div className="text-center">
              <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <StarIcon className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-2">Premium Locations</h3>
              <p className="text-neutral-600">Access to exclusive photography locations and wildlife hotspots</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tours Section */}
      <div className="py-16 bg-neutral-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Photography Experiences</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Choose from our carefully selected photography experiences that offer 
              authentic connections with local communities and traditions.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-800"></div>
              <span className="ml-4 text-xl text-neutral-600">Loading photography tours...</span>
            </div>
          ) : photographyTours.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {photographyTours.map((tour) => (
              <TourCard
                key={tour.id}
                tour={tour}
                showQuickBook={true}
              />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-neutral-500 text-lg mb-4">
                No Photography tours available at the moment.
              </div>
              <p className="text-neutral-400 mb-6">
                Check back soon for new photography experiences, or explore our other tour categories.
              </p>
              <Link
                to="/tours"
                className="inline-flex items-center bg-primary-800 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
              >
                View All Tours
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Photography Tips Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-primary-900 mb-4">Photography Tips & Techniques</h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              Learn professional wildlife photography techniques from our expert guides. 
              Master the art of capturing Africa's incredible wildlife and landscapes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-primary-800 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl font-bold">1</span>
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-2">Lighting Mastery</h3>
              <p className="text-neutral-600">Learn to work with natural light and golden hour photography techniques</p>
            </div>
            <div className="text-center">
              <div className="bg-primary-800 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl font-bold">2</span>
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-2">Wildlife Behavior</h3>
              <p className="text-neutral-600">Understand animal behavior to anticipate and capture the perfect moment</p>
            </div>
            <div className="text-center">
              <div className="bg-primary-800 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl font-bold">3</span>
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-2">Equipment & Settings</h3>
              <p className="text-neutral-600">Master camera settings and equipment for optimal wildlife photography</p>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="py-16 bg-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Capture Africa?</h2>
          <p className="text-xl text-neutral-200 mb-8 max-w-3xl mx-auto">
            Join our photography tours and learn to capture stunning images of Africa's wildlife and landscapes. 
            From beginner tips to advanced techniques, we'll help you create amazing photographs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/tours"
              className="bg-white text-primary-800 hover:bg-neutral-100 px-8 py-3 rounded-lg font-medium transition-colors duration-200"
            >
              View All Tours
            </Link>
            <Link
              to="/contact"
              className="bg-primary-700 hover:bg-primary-600 text-white px-8 py-3 rounded-lg font-medium transition-colors duration-200"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotographyTours;